// src/components/shared/NotificationBell.jsx
// Campanita de notificaciones real del dashboard. Reemplaza la tarjeta estática
// que mostraba siempre "0" (user?.stats?.notifications, que nunca se llenaba).
// Sigue el mismo patrón de fetch + Authorization Bearer que ya usa el resto de
// la app (ver apiFetch en EmpleadosModule.jsx) y navega entre módulos con el
// mismo mecanismo por estado que usa el resto del dashboard (navigateToSection
// de AppContext), sin usar react-router.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell } from 'lucide-react';
import { useApp } from '../../context/AppContext';

const API_URL = process.env.REACT_APP_API_URL;
const getToken = () => localStorage.getItem('dths_token');

const apiFetch = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}${path}`, {
    headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json', ...opts.headers },
    ...opts,
  });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || e.message || `HTTP ${res.status}`); }
  return res.json();
};

const tiempoRelativo = (fecha) => {
  if (!fecha) return '';
  const f = new Date(fecha);
  const diffMs = Date.now() - f.getTime();
  const diffSeg = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSeg / 60);
  const diffHoras = Math.floor(diffMin / 60);
  const diffDias = Math.floor(diffHoras / 24);
  if (diffSeg < 60) return 'hace un momento';
  if (diffMin < 60) return `hace ${diffMin} min`;
  if (diffHoras < 24) return `hace ${diffHoras} h`;
  if (diffDias < 7) return `hace ${diffDias} d`;
  return f.toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' });
};

const NotificationBell = () => {
  const { navigateToSection } = useApp();
  const [count, setCount] = useState(0);
  const [notificaciones, setNotificaciones] = useState([]);
  const [abierto, setAbierto] = useState(false);
  const [cargando, setCargando] = useState(false);
  const contenedorRef = useRef(null);

  const cargarConteo = useCallback(async () => {
    try {
      const data = await apiFetch('/notificaciones/no-leidas/count');
      if (data?.success) setCount(data.total || 0);
    } catch (err) {
      console.error('Error obteniendo conteo de notificaciones:', err.message);
    }
  }, []);

  const cargarNotificaciones = useCallback(async () => {
    setCargando(true);
    try {
      const data = await apiFetch('/notificaciones');
      if (data?.success) setNotificaciones(data.notificaciones || []);
    } catch (err) {
      console.error('Error obteniendo notificaciones:', err.message);
    } finally {
      setCargando(false);
    }
  }, []);

  // Carga inicial + refresco cada 60s del contador (para la campanita, aunque
  // el panel esté cerrado).
  useEffect(() => {
    cargarConteo();
    const intervalo = setInterval(cargarConteo, 60000);
    return () => clearInterval(intervalo);
  }, [cargarConteo]);

  // Al abrir el panel, trae la lista completa.
  useEffect(() => {
    if (abierto) cargarNotificaciones();
  }, [abierto, cargarNotificaciones]);

  // Cerrar el panel al hacer clic fuera.
  useEffect(() => {
    const handleClickFuera = (e) => {
      if (contenedorRef.current && !contenedorRef.current.contains(e.target)) setAbierto(false);
    };
    document.addEventListener('mousedown', handleClickFuera);
    return () => document.removeEventListener('mousedown', handleClickFuera);
  }, []);

  const handleClickNotificacion = async (notif) => {
    if (!notif.Leida) {
      try {
        await apiFetch(`/notificaciones/${notif.NotificacionID}/leida`, { method: 'PUT' });
        setNotificaciones(prev => prev.map(n => n.NotificacionID === notif.NotificacionID ? { ...n, Leida: true } : n));
        setCount(prev => Math.max(0, prev - 1));
      } catch (err) {
        console.error('Error marcando notificación como leída:', err.message);
      }
    }
    setAbierto(false);
    if (notif.Url) navigateToSection(notif.Url);
  };

  const handleMarcarTodas = async (e) => {
    e.stopPropagation();
    try {
      await apiFetch('/notificaciones/marcar-todas-leidas', { method: 'PUT' });
      setNotificaciones(prev => prev.map(n => ({ ...n, Leida: true })));
      setCount(0);
    } catch (err) {
      console.error('Error marcando todas como leídas:', err.message);
    }
  };

  return (
    <div ref={contenedorRef} style={{ position: 'relative' }}>
      <div
        onClick={() => setAbierto(prev => !prev)}
        role="button"
        aria-label="Notificaciones"
        style={{
          background: 'linear-gradient(135deg, #dc143c 0%, #ef4444 100%)',
          borderRadius: '1rem',
          padding: '1.75rem',
          boxShadow: '0 4px 12px rgba(220, 20, 60, 0.25), 0 1px 3px rgba(0, 0, 0, 0.1)',
          position: 'relative',
          overflow: 'hidden',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          cursor: 'pointer'
        }}
      >
        <div style={{
          position: 'absolute',
          top: '-20px',
          right: '-20px',
          width: '100px',
          height: '100px',
          background: 'rgba(255, 255, 255, 0.06)',
          borderRadius: '50%',
          filter: 'blur(20px)'
        }}></div>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <p style={{
            color: 'rgba(255, 255, 255, 0.9)',
            fontSize: '0.8125rem',
            fontWeight: '600',
            margin: '0 0 0.5rem 0',
            textTransform: 'uppercase',
            letterSpacing: '0.08em'
          }}>Notificaciones</p>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <p style={{
              fontSize: '1.375rem',
              fontWeight: '600',
              color: '#ffffff',
              margin: 0,
              letterSpacing: '-0.01em'
            }}>{count}</p>
            <div style={{
              width: '2.75rem',
              height: '2.75rem',
              background: 'rgba(255, 255, 255, 0.18)',
              borderRadius: '0.625rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
            }}>
              <Bell style={{ width: '1.375rem', height: '1.375rem', color: '#ffffff' }} />
            </div>
          </div>
        </div>
      </div>

      {abierto && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 0.5rem)',
          right: 0,
          width: '360px',
          maxWidth: '90vw',
          background: '#ffffff',
          borderRadius: '0.75rem',
          boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
          zIndex: 100,
          overflow: 'hidden',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0.875rem 1rem',
            borderBottom: '1px solid #e5e7eb',
            background: '#f9fafb'
          }}>
            <span style={{ fontWeight: 600, color: '#111827', fontSize: '0.9375rem' }}>Notificaciones</span>
            <button
              onClick={handleMarcarTodas}
              style={{ border: 'none', background: 'none', color: '#2563eb', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer', padding: 0 }}
            >
              Marcar todas como leídas
            </button>
          </div>
          <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
            {cargando && (
              <p style={{ padding: '1.5rem 1rem', color: '#6b7280', fontSize: '0.875rem', textAlign: 'center', margin: 0 }}>Cargando...</p>
            )}
            {!cargando && notificaciones.length === 0 && (
              <p style={{ padding: '1.5rem 1rem', color: '#6b7280', fontSize: '0.875rem', textAlign: 'center', margin: 0 }}>No tienes notificaciones</p>
            )}
            {!cargando && notificaciones.map(n => (
              <div
                key={n.NotificacionID}
                onClick={() => handleClickNotificacion(n)}
                style={{
                  display: 'flex',
                  gap: '0.625rem',
                  padding: '0.875rem 1rem',
                  borderBottom: '1px solid #f1f5f9',
                  cursor: 'pointer',
                  background: n.Leida ? '#ffffff' : '#eff6ff'
                }}
              >
                <div style={{ flexShrink: 0, marginTop: '0.4rem' }}>
                  <span style={{
                    display: 'inline-block',
                    width: '0.5rem',
                    height: '0.5rem',
                    borderRadius: '50%',
                    background: n.Leida ? 'transparent' : '#2563eb'
                  }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: n.Leida ? 500 : 700, color: '#111827' }}>{n.Titulo}</p>
                  <p style={{ margin: '0.125rem 0 0 0', fontSize: '0.8125rem', color: '#4b5563', lineHeight: 1.4 }}>{n.Mensaje}</p>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#9ca3af' }}>{tiempoRelativo(n.FechaCreacion)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
